// pages/instructor/AddCourse.js
import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Paper,
  Chip,
  Breadcrumbs,
  Link,
  Stack,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Home,
  Add,
  Upload,
  Image,
  Description,
  DateRange,
  School,
  ArrowForward,
  CloudUpload,
  Delete,
  Preview,
} from "@mui/icons-material";
import SideBar from "./components/InstructorSideBar";
import { uploadFileService } from "../../services/Request";
import "../../styles/AddCourse.css";

const CourseLevel = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advance", label: "Advance" },
];

const AddCourse = () => {
  const nav = useNavigate();
  const fileInputRef = useRef(null);
  const [level, setLevel] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [instructorId, setInstructorId] = useState("");
  const [instructorName, setInstructorName] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Form data
  const [formData, setFormData] = useState({
    courseName: "",
    startDate: "",
    endDate: "",
    description: "",
    courseOutline: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      try {
        const data = JSON.parse(user);
        setInstructorName(data.firstname + " " + data.lastname);
        setInstructorId(data.userId);
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
  }, []);

  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size / 1024 / 1024 > 1) {
      setSnackbar({
        open: true,
        message: "File size exceeds 1 MB. Please select a smaller file.",
        severity: "error",
      });
      return;
    }

    setUploading(true);
    uploadFileService(file)
      .then((response) => {
        setUploading(false);
        if (response.status === true) {
          setImageUrl(response.location);
          setSnackbar({
            open: true,
            message: "Image uploaded successfully!",
            severity: "success",
          });
        } else {
          setSnackbar({
            open: true,
            message: "Error uploading image. Please try again.",
            severity: "error",
          });
        }
      })
      .catch((error) => {
        setUploading(false);
        setSnackbar({
          open: true,
          message: "Error uploading image. Please try again.",
          severity: "error",
        });
      });
  };

  const handleRemoveImage = () => {
    setImageUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.courseName.trim())
      newErrors.courseName = "Course name is required";
    if (!formData.startDate) newErrors.startDate = "Start date is required";
    if (!formData.endDate) newErrors.endDate = "End date is required";
    if (!level) newErrors.level = "Please select a difficulty level";
    if (!formData.description.trim())
      newErrors.description = "Course description is required";
    if (!formData.courseOutline.trim())
      newErrors.courseOutline = "Course outline is required";
    if (!imageUrl) newErrors.image = "Please upload a course image";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields.",
        severity: "error",
      });
      return;
    }

    setLoading(true);
    const details = {
      instructorId: instructorId,
      instructorName: instructorName,
      courseName: formData.courseName,
      startDate: formData.startDate,
      endDate: formData.endDate,
      courseLevel: level,
      description: formData.description,
      courseOutline: formData.courseOutline,
      image: imageUrl,
    };

    localStorage.setItem("addcourse", JSON.stringify(details));
    setTimeout(() => {
      setLoading(false);
      nav("/coursepreview");
    }, 500);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        backgroundColor: "#F7F8F2",
      }}
    >
      {/* Sidebar */}
      <Box
        sx={{
          width: { xs: 0, md: 280 },
          flexShrink: 0,
          display: { xs: "none", md: "block" },
        }}
      >
        <SideBar />
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            backgroundColor: "#ffffff",
            borderBottom: "1px solid rgba(43, 173, 101, 0.06)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>
              <Breadcrumbs
                separator={
                  <ArrowForward
                    sx={{ fontSize: 14, color: "rgba(26,26,26,0.2)" }}
                  />
                }
                aria-label="breadcrumb"
                sx={{ mb: 1 }}
              >
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#2BAD65",
                    },
                  }}
                >
                  <Home sx={{ fontSize: 16 }} />
                  Home
                </Link>
                <Link
                  underline="hover"
                  color="rgba(26, 26, 26, 0.4)"
                  href="/instructor/dashboard"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: "0.85rem",
                    "&:hover": {
                      color: "#2BAD65",
                    },
                  }}
                >
                  <School sx={{ fontSize: 16 }} />
                  Dashboard
                </Link>
                <Typography
                  sx={{
                    color: "#2BAD65",
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Add sx={{ fontSize: 16 }} />
                  Add Course
                </Typography>
              </Breadcrumbs>

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a1a",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                Create New Course
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(26, 26, 26, 0.4)",
                  mt: 0.5,
                }}
              >
                Fill in the details below to create a new course
              </Typography>
            </Grid>

            <Grid item>
              <Chip
                icon={<School sx={{ fontSize: 16 }} />}
                label={instructorName || "Instructor"}
                sx={{
                  backgroundColor: "rgba(43, 173, 101, 0.08)",
                  color: "#2BAD65",
                  fontWeight: 600,
                  border: "1px solid rgba(43, 173, 101, 0.1)",
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Form Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            p: { xs: 2, md: 3 },
          }}
        >
          <Paper
            elevation={0}
            component="form"
            onSubmit={handleSubmit}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              backgroundColor: "#ffffff",
              border: "1px solid rgba(43, 173, 101, 0.06)",
              maxWidth: 900,
            }}
          >
            <Grid container spacing={3}>
              {/* Image Upload */}
              <Grid item xs={12}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: "#1a1a1a",
                    mb: 1,
                  }}
                >
                  Course Image
                </Typography>
                <Box
                  onClick={handleUploadClick}
                  sx={{
                    border: "2px dashed rgba(43, 173, 101, 0.2)",
                    borderRadius: 3,
                    p: 3,
                    textAlign: "center",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    backgroundColor: imageUrl
                      ? "rgba(43, 173, 101, 0.02)"
                      : "transparent",
                    "&:hover": {
                      borderColor: "#2BAD65",
                      backgroundColor: "rgba(43, 173, 101, 0.04)",
                    },
                    position: "relative",
                  }}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept="image/png,image/jpeg,image/gif"
                  />

                  {imageUrl ? (
                    <Box sx={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={imageUrl}
                        alt="Course"
                        style={{
                          maxWidth: "100%",
                          maxHeight: 200,
                          borderRadius: 8,
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveImage();
                        }}
                        sx={{
                          position: "absolute",
                          top: -10,
                          right: -10,
                          backgroundColor: "#FF6B6B",
                          color: "#ffffff",
                          "&:hover": {
                            backgroundColor: "#E55555",
                          },
                          width: 28,
                          height: 28,
                        }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  ) : (
                    <Box>
                      {uploading ? (
                        <CircularProgress sx={{ color: "#2BAD65" }} />
                      ) : (
                        <>
                          <CloudUpload
                            sx={{
                              fontSize: 48,
                              color: "rgba(43, 173, 101, 0.3)",
                              mb: 1,
                            }}
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 600,
                              color: "#2BAD65",
                            }}
                          >
                            Click to upload
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: "rgba(26, 26, 26, 0.3)",
                            }}
                          >
                            or drag and drop
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "rgba(26, 26, 26, 0.2)",
                              display: "block",
                              mt: 1,
                            }}
                          >
                            PNG, JPG or GIF (max. 800x400px, 1MB)
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                </Box>
                {errors.image && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#FF6B6B",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    {errors.image}
                  </Typography>
                )}
              </Grid>

              {/* Course Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="courseName"
                  label="Course Name"
                  value={formData.courseName}
                  onChange={handleChange}
                  error={!!errors.courseName}
                  helperText={errors.courseName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <School sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.02)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />
              </Grid>

              {/* Dates */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="startDate"
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRange sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.02)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name="endDate"
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                  error={!!errors.endDate}
                  helperText={errors.endDate}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRange sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.02)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />
              </Grid>

              {/* Course Level */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  select
                  name="courseLevel"
                  label="Course Difficulty Level"
                  value={level}
                  onChange={(e) => setLevel(e.target.value)}
                  error={!!errors.level}
                  helperText={errors.level}
                  placeholder="Choose the level of difficulty"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <School sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.02)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                >
                  {CourseLevel.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="description"
                  label="Course Description"
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={handleChange}
                  error={!!errors.description}
                  helperText={errors.description}
                  placeholder="Give a brief description of this course"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.02)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />
              </Grid>

              {/* Course Outline */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="courseOutline"
                  label="Course Outlines"
                  multiline
                  rows={3}
                  value={formData.courseOutline}
                  onChange={handleChange}
                  error={!!errors.courseOutline}
                  helperText={errors.courseOutline}
                  placeholder="Enter the course outline (e.g., Week 1: Introduction, Week 2: Advanced Topics)"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Description sx={{ color: "rgba(26, 26, 26, 0.2)" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "rgba(43, 173, 101, 0.02)",
                      borderRadius: 2,
                      "& fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.08)",
                      },
                      "&:hover fieldset": {
                        borderColor: "rgba(43, 173, 101, 0.15)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#2BAD65",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(26, 26, 26, 0.4)",
                    },
                  }}
                />
              </Grid>

              {/* Buttons */}
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={20} /> : <Preview />
                    }
                    sx={{
                      flex: 1,
                      py: 1.5,
                      backgroundColor: "#2BAD65",
                      color: "#ffffff",
                      fontWeight: 600,
                      fontSize: "1rem",
                      borderRadius: 2,
                      "&:hover": {
                        backgroundColor: "#239A56",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 20px rgba(43, 173, 101, 0.3)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Processing..." : "Preview Course"}
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => nav("/instructor/courses")}
                    sx={{
                      px: 4,
                      py: 1.5,
                      color: "rgba(26, 26, 26, 0.6)",
                      borderColor: "rgba(43, 173, 101, 0.15)",
                      borderRadius: 2,
                      fontWeight: 600,
                      "&:hover": {
                        borderColor: "#2BAD65",
                        backgroundColor: "rgba(43, 173, 101, 0.04)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Cancel
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{
            backgroundColor:
              snackbar.severity === "success" ? "#2BAD65" : "#FF6B6B",
            color: "#ffffff",
            "& .MuiAlert-icon": {
              color: "#ffffff",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddCourse;
